import { createContext, useContext, useEffect, useState } from "react";
import { getSiteSettings } from "@/lib/api";
import { CABINET } from "@/lib/content";

// Static fallbacks come from the hardcoded content — used until the API
// answers or if the API is unavailable. This keeps the site rendering fully
// even offline.
const DEFAULT_SETTINGS = {
    opening_banner_enabled: false,
    opening_banner_text: "",
    phone: CABINET.phone,
    phone_intl: CABINET.phoneIntl,
    email: CABINET.email,
    address_street: CABINET.address,
    address_city: CABINET.city,
    canton: CABINET.canton,
    hours: "Lundi–Jeudi · 8h00–12h00 / 13h30–18h00\nVendredi · 8h00–12h00\nSamedi · sur rendez-vous",
    tarif_cabinet: "",
    tarif_domicile: "",
    tarif_note: "",
};

const SiteSettingsContext = createContext({
    settings: DEFAULT_SETTINGS,
    loading: true,
    refresh: () => { },
});

export function SiteSettingsProvider({ children }) {
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        getSiteSettings()
            .then((s) => setSettings((prev) => ({ ...prev, ...s })))
            .catch(() => { })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    return (
        <SiteSettingsContext.Provider value={{ settings, loading, refresh }}>
            {children}
        </SiteSettingsContext.Provider>
    );
}

export function useSiteSettings() {
    return useContext(SiteSettingsContext);
}

/**
 * Convenience hook returning a merged CABINET-shaped object that
 * reflects any admin edits (phone, email, address, hours). Any component
 * previously importing CABINET can swap to this hook with no other change.
 */
export function useCabinet() {
    const { settings } = useSiteSettings();
    // We import CABINET lazily to avoid circular concerns
    // eslint-disable-next-line global-require
    const { CABINET } = require("@/lib/content");
    return {
        ...CABINET,
        phone: settings.phone || CABINET.phone,
        phoneIntl: settings.phone_intl || CABINET.phoneIntl,
        email: settings.email || CABINET.email,
        address: settings.address_street || CABINET.address,
        city: settings.address_city || CABINET.city,
        canton: settings.canton || CABINET.canton,
        hours: settings.hours || "",
        tarifCabinet: settings.tarif_cabinet || "",
        tarifDomicile: settings.tarif_domicile || "",
        tarifNote: settings.tarif_note || "",
    };
}

export { DEFAULT_SETTINGS };
